import {
  Padding,
  paddingMap,
  paddingXMap,
  paddingYMap,
} from "apps/website/maps/Spacing.map";
import {
  BorderRadius,
  borderRadiusMap,
  Border,
  borderMap,
  BorderStyle,
  borderStyleMap,
} from "apps/website/maps/Border.map";
import {
  Align,
  Justify,
  Direction,
  Gap,
  defaultAlignClassMap,
  defaultJustifyClassMap,
  defaultDirectionClassMap,
  gapClassMap,
  gapXClassMap,
  gapYClassMap,
} from "apps/website/maps/Flex.map";

export interface StyleOptions {
  padding?: Padding;
  paddingX?: Padding;
  paddingY?: Padding;
  borderRadius?: BorderRadius;
  align?: Align;
  justify?: Justify;
  direction?: Direction;
  gap?: Gap;
  gapX?: Gap;
  gapY?: Gap;
  border?: Border;
  borderStyle?: BorderStyle;
}

/**
 * Helper function to determine padding classes based on style options
 * Prioritizes paddingX/paddingY over uniform padding
 *
 * @param styleOptions - The style options to use
 * @returns The padding classes
 */
export const getPaddingClasses = (styleOptions: StyleOptions = {}): string[] => {
  const classes: string[] = [];

  if (styleOptions.paddingX !== undefined) {
    classes.push(paddingXMap[styleOptions.paddingX]);
  }
  if (styleOptions.paddingY !== undefined) {
    classes.push(paddingYMap[styleOptions.paddingY]);
  }
  if (classes.length > 0) {
    return classes;
  }

  const paddingClass = paddingMap[styleOptions.padding ?? 4];
  return [ paddingClass ];
};

/**
 * Helper function to determine border radius class based on style options
 *
 * @param styleOptions - The style options to use
 * @returns The border radius class
 */
export const getBorderRadiusClass = (styleOptions: StyleOptions = {}): string => borderRadiusMap[styleOptions.borderRadius ?? "2xl"];

/**
 * Helper function to determine flex classes based on style options
 *
 * @param styleOptions - The style options to use
 * @returns The flex classes
 */
export const getFlexClasses = (styleOptions: StyleOptions = {}): string[] => {
  const classes: string[] = [];

  if (styleOptions.direction !== undefined) {
    classes.push(defaultDirectionClassMap[styleOptions.direction]);
  }

  if (styleOptions.align !== undefined) {
    classes.push(defaultAlignClassMap[styleOptions.align]);
  }
  if (styleOptions.justify !== undefined) {
    classes.push(defaultJustifyClassMap[styleOptions.justify]);
  }

  return classes;
};

/**
 * Helper function to determine gap classes based on style options
 * Prioritizes gapX/gapY over uniform gap
 *
 * @param styleOptions - The style options to use
 * @returns The gap classes
 */
export const getGapClasses = (styleOptions: StyleOptions = {}): string[] => {
  const classes: string[] = [];

  if (styleOptions.gapX !== undefined) {
    classes.push(gapXClassMap[styleOptions.gapX]);
  }
  if (styleOptions.gapY !== undefined) {
    classes.push(gapYClassMap[styleOptions.gapY]);
  }
  if (classes.length > 0) {
    return classes;
  }

  if (styleOptions.gap !== undefined) {
    classes.push(gapClassMap[styleOptions.gap]);
  }

  return classes;
};

export const getBorderClass = (styleOptions: StyleOptions = {}): string[] => {
  const classes: string[] = [];
  
  // Add border width class
  classes.push(borderMap[styleOptions.border ?? "none"]);
  
  // Add border style class if border is not "none" and borderStyle is specified
  if (styleOptions.border !== "none" && styleOptions.borderStyle) {
    classes.push(borderStyleMap[styleOptions.borderStyle]);
  }
  
  return classes;
};

/**
 * Default styling for sign-up flow quiz images
 */
export const signUpFlowImageClasses = "w-[342px] h-[140px] md:scale-[1.2] overflow-hidden";
